export const COOKIE_CONSENT_STORAGE_KEY = 'thetavern.cookieConsent.v1';

export const cookieCategories = {
  essential: {
    labelKey: 'cookies.categories.essential.label',
    descriptionKey: 'cookies.categories.essential.description',
    required: true,
  },
  analytics: {
    labelKey: 'cookies.categories.analytics.label',
    descriptionKey: 'cookies.categories.analytics.description',
    required: false,
  },
  performance: {
    labelKey: 'cookies.categories.performance.label',
    descriptionKey: 'cookies.categories.performance.description',
    required: false,
  },
  marketing: {
    labelKey: 'cookies.categories.marketing.label',
    descriptionKey: 'cookies.categories.marketing.description',
    required: false,
  },
};

export const optionalCookieCategories = ['analytics', 'performance', 'marketing'];

export const cookieInventory = [
  {
    name: 'tavern_token',
    category: 'essential',
    storage: 'localStorage',
    purpose: 'Stores the JWT used in Authorization headers for authenticated API calls.',
  },
  {
    name: 'thetavern.locale',
    category: 'essential',
    storage: 'localStorage',
    purpose: 'Stores the selected interface language.',
  },
  {
    name: 'thetavern:night-draft:v1:*',
    category: 'essential',
    storage: 'localStorage',
    purpose: 'Stores in-progress game night drafts so users do not lose form work.',
  },
  {
    name: 'thetavern.headerProfile',
    category: 'essential',
    storage: 'sessionStorage',
    purpose: 'Caches header profile display data for the current browser session.',
  },
  {
    name: 'thetavern.installPromptSnoozedUntil / thetavern.iosInstallPromptSnoozedUntil',
    category: 'essential',
    storage: 'localStorage',
    purpose: 'Stores the temporary cooldown for install prompt reminders.',
  },
  {
    name: 'thetavern.installPromptNeverAskAgain / thetavern.iosInstallPromptNeverAskAgain',
    category: 'essential',
    storage: 'localStorage',
    purpose: 'Stores the permanent install prompt dismissal choice.',
  },
  {
    name: COOKIE_CONSENT_STORAGE_KEY,
    category: 'essential',
    storage: 'localStorage',
    purpose: 'Stores the user cookie consent preferences.',
  },
];

// No analytics, marketing, monitoring, or Sentry provider is initialized today.
// Future providers must check canUseCookieCategory(category) before loading.
export const optionalProviderInventory = [];

const defaultPreferences = {
  essential: true,
  analytics: false,
  performance: false,
  marketing: false,
  consentedAt: '',
};

const isBrowserStorageAvailable = () =>
  typeof window !== 'undefined' && Boolean(window.localStorage);

export const normalizeCookiePreferences = (preferences = {}) => ({
  ...defaultPreferences,
  analytics: Boolean(preferences.analytics),
  performance: Boolean(preferences.performance),
  marketing: Boolean(preferences.marketing),
  essential: true,
  consentedAt:
    typeof preferences.consentedAt === 'string' && preferences.consentedAt
      ? preferences.consentedAt
      : new Date().toISOString(),
});

export const readCookiePreferences = () => {
  if (!isBrowserStorageAvailable()) return null;

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);

    return raw ? normalizeCookiePreferences(JSON.parse(raw)) : null;
  } catch {
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    return null;
  }
};

export const saveCookiePreferences = (preferences) => {
  const normalized = normalizeCookiePreferences({
    ...preferences,
    consentedAt: new Date().toISOString(),
  });

  if (isBrowserStorageAvailable()) {
    window.localStorage.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify(normalized)
    );
    window.dispatchEvent(
      new CustomEvent('thetavern:cookie-consent-updated', {
        detail: normalized,
      })
    );
  }

  return normalized;
};

export const acceptAllCookies = () =>
  saveCookiePreferences({
    analytics: true,
    performance: true,
    marketing: true,
  });

export const rejectOptionalCookies = () =>
  saveCookiePreferences({
    analytics: false,
    performance: false,
    marketing: false,
  });

export const canUseCookieCategory = (category) => {
  if (category === 'essential') return true;

  const preferences = readCookiePreferences();

  return Boolean(preferences?.[category]);
};
